'use client';

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from '@hookform/resolvers/zod';
import * as Popover from '@radix-ui/react-popover';
import { FeatherAlertTriangle, FeatherChevronDown, FeatherExternalLink, FeatherInfo, FeatherPackage, FeatherSearch } from '@subframe/core';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { fetchActiveProducts, selectActiveProducts, selectActiveProductsLoading } from '@/features/admin-geral/store/activeProducts';
import type { ActiveProduct } from '@/features/admin-geral/types/activeProduct';
import { Alert, Button, Card, Select, TextArea, TextField } from '@/shared/components/ui';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { MockEligibleTenant } from '../lib/mockData';
import { fetchEligibleTenants } from '../store';
import type { IniciarTrialFormData, TrialProductBehavior } from '../types';
import { calcularDiasPeriodo, dataAtualISO, isDataFutura, isDataHojeOuFutura } from '../utils/trialDateUtils';
const schema = z.object({
  tenantId: z.string().min(1, 'Selecione um cliente'),
  produtoId: z.string().min(1, 'Selecione um produto'),
  dataInicio: z.string().min(1, 'Informe a data de início').refine(value => isDataHojeOuFutura(value), 'A data de início não pode ser no passado'),
  dataExpiracao: z.string().min(1, 'Informe a data de expiração').refine(value => isDataFutura(value), 'A data de expiração deve ser futura'),
  alertaVencimentoDias: z.number({
    error: 'Informe um número válido'
  }).min(0, 'Não pode ser negativo').nullable(),
  observacao: z.string().nullable(),
  metodoPagamento: z.enum(['PIX', 'BOLETO', 'CARTAO'], {
    error: 'Selecione o método de pagamento'
  }),
  diaVencimento: z.number({
    error: 'Informe o dia de vencimento'
  }).min(1, 'O dia deve ser entre 1 e 28').max(28, 'O dia deve ser entre 1 e 28')
}).refine(data => data.dataExpiracao > data.dataInicio, {
  message: 'A data de expiração deve ser posterior à data de início',
  path: ['dataExpiracao']
});
const TRIAL_BEHAVIOR_LABEL: Record<TrialProductBehavior, string> = {
  FREE: 'Uso liberado sem cobrança',
  CHARGE: 'Uso liberado com fatura R$ 0,00',
  BLOCK: 'Bloqueado durante o trial'
};
const addDaysIso = (baseIsoDate: string, days: number): string => {
  const [year, month, day] = baseIsoDate.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
};
type FormSchema = z.infer<typeof schema>;
interface TenantPickerProps {
  value: string;
  onValueChange: (value: string) => void;
  tenants: MockEligibleTenant[];
  loading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  error?: boolean;
  helpText?: React.ReactNode;
}
function TenantPicker(t0) {
  const $ = _c(43);
  const {
    value,
    onValueChange,
    tenants,
    loading,
    search,
    onSearchChange,
    error,
    helpText
  } = t0;
  const [open, setOpen] = useState(false);
  const [selectedNameCache, setSelectedNameCache] = useState(null);
  const searchInputRef = useRef(null);
  let t1;
  bb0: {
    if (!value) {
      t1 = null;
      break bb0;
    }
    let t2;
    if ($[0] !== tenants || $[1] !== value) {
      let t3;
      if ($[3] !== value) {
        t3 = t => t.id === value;
        $[3] = value;
        $[4] = t3;
      } else {
        t3 = $[4];
      }
      t2 = tenants.find(t3);
      $[0] = tenants;
      $[1] = value;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    const found = t2;
    t1 = found?.nome ?? selectedNameCache;
  }
  const displayName = t1;
  let t2;
  if ($[5] !== onSearchChange || $[6] !== onValueChange) {
    t2 = tenant => {
      setSelectedNameCache(tenant.nome);
      onValueChange(tenant.id);
      setOpen(false);
      onSearchChange("");
    };
    $[5] = onSearchChange;
    $[6] = onValueChange;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleSelect = t2;
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-caption-bold font-caption-bold text-default-font">Cliente *</span>;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = `bg-default-background relative flex h-10 w-full items-center gap-2 rounded-[8px] border border-solid px-3 pr-10 text-left transition-all duration-200 focus:ring-4 focus:outline-none ${error ? "border-error-600 focus:border-error-600 focus:ring-error-600/10" : "focus:border-brand-600 focus:ring-brand-600/10 border-neutral-300"}`;
  const t5 = `text-body font-body w-full truncate ${displayName ? "text-default-font" : "text-neutral-400"}`;
  const t6 = displayName ?? "Selecione um cliente";
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <span className={t5}>{t6}</span>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <FeatherChevronDown className="text-subtext-color pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2" />;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t4 || $[14] !== t7) {
    t9 = <Popover.Trigger asChild={true}><button type="button" className={t4}>{t7}{t8}</button></Popover.Trigger>;
    $[13] = t4;
    $[14] = t7;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== error || $[17] !== helpText) {
    t10 = helpText ? <span className={`text-caption font-caption ${error ? "text-error-700" : "text-subtext-color"}`}>{helpText}</span> : null;
    $[16] = error;
    $[17] = helpText;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t9) {
    t11 = <div className="flex flex-col gap-1">{t3}{t9}{t10}</div>;
    $[19] = t10;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = event => {
      event.preventDefault();
      searchInputRef.current?.focus();
    };
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <FeatherSearch className="h-4 w-4 shrink-0 text-neutral-400" />;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== onSearchChange) {
    t14 = event_0 => onSearchChange(event_0.target.value);
    $[24] = onSearchChange;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== search || $[27] !== t14) {
    t15 = <div className="flex items-center gap-2 border-b border-solid border-neutral-100 px-3 py-2">{t13}<input ref={searchInputRef} type="text" value={search} onChange={t14} placeholder="Buscar cliente..." className="text-body font-body text-default-font w-full bg-transparent outline-none placeholder:text-neutral-400" /></div>;
    $[26] = search;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== handleSelect || $[30] !== loading || $[31] !== tenants || $[32] !== value) {
    t16 = loading ? <span className="text-caption font-caption text-subtext-color px-3 py-3">Carregando...</span> : tenants.length === 0 ? <span className="text-caption font-caption text-subtext-color px-3 py-3">Nenhum cliente encontrado</span> : tenants.map(tenant_0 => {
      const isSelected = tenant_0.id === value;
      return <button key={tenant_0.id} type="button" onClick={() => handleSelect(tenant_0)} className={`text-body font-body flex items-center justify-between gap-2 px-3 py-2 text-left transition-colors ${isSelected ? "bg-brand-50 text-brand-700" : "text-default-font hover:bg-neutral-100"}`}><span className="truncate">{tenant_0.nome}</span>{tenant_0.emTrialAtivo ? <span className="text-caption text-warning-700 shrink-0">em trial ativo</span> : null}</button>;
    });
    $[29] = handleSelect;
    $[30] = loading;
    $[31] = tenants;
    $[32] = value;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t16) {
    t17 = <div className="flex max-h-[300px] flex-col overflow-y-auto py-1">{t16}</div>;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t15 || $[37] !== t17) {
    t18 = <Popover.Portal><Popover.Content align="start" sideOffset={8} onOpenAutoFocus={t12} className="z-[10010] flex w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden rounded-[16px] border border-solid border-neutral-200 bg-white shadow-lg">{t15}{t17}</Popover.Content></Popover.Portal>;
    $[36] = t15;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== open || $[40] !== t11 || $[41] !== t18) {
    t19 = <Popover.Root open={open} onOpenChange={setOpen}>{t11}{t18}</Popover.Root>;
    $[39] = open;
    $[40] = t11;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  return t19;
}
interface TrialFormProps {
  saving: boolean;
  onSubmit: (data: IniciarTrialFormData) => void;
  onCancel: () => void;
}
const DEFAULT_ALERT_DAYS = 7;
const TENANT_SEARCH_LIMIT = 30;
const TENANT_SEARCH_DEBOUNCE_MS = 300;
export function TrialForm({
  saving,
  onSubmit,
  onCancel
}: TrialFormProps) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [tenants, setTenants] = useState<MockEligibleTenant[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [tenantSearch, setTenantSearch] = useState('');
  const [selectedTenant, setSelectedTenant] = useState<MockEligibleTenant | null>(null);
  const produtos = useAppSelector(selectActiveProducts);
  const produtosLoading = useAppSelector(selectActiveProductsLoading);
  const [expiracaoCustomizada, setExpiracaoCustomizada] = useState(false);
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: {
      errors,
      isDirty
    }
  } = useForm<FormSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      tenantId: '',
      produtoId: '',
      dataInicio: dataAtualISO(),
      dataExpiracao: '',
      alertaVencimentoDias: DEFAULT_ALERT_DAYS,
      observacao: '',
      metodoPagamento: 'PIX',
      diaVencimento: 10
    }
  });
  const tenantId = watch('tenantId');
  const produtoId = watch('produtoId');
  const dataInicio = watch('dataInicio');
  const dataExpiracao = watch('dataExpiracao');
  const produtoSelecionado: ActiveProduct | null = produtos.find(p => p.id === produtoId) ?? null;
  const categoriaProdutoSelecionado = produtoSelecionado?.category ?? null;
  const produtoTrialBehavior: TrialProductBehavior | null = produtoSelecionado?.trialBehavior === 'FREE' || produtoSelecionado?.trialBehavior === 'CHARGE' || produtoSelecionado?.trialBehavior === 'BLOCK' ? produtoSelecionado.trialBehavior : null;
  const periodoDias = useMemo(() => {
    if (!dataInicio || !dataExpiracao) return 0;
    if (dataExpiracao <= dataInicio) return 0;
    return calcularDiasPeriodo(dataInicio, dataExpiracao);
  }, [dataInicio, dataExpiracao]);
  const contratoEmConflito = useMemo(() => {
    if (!selectedTenant || !categoriaProdutoSelecionado) return null;
    const naCategoria = selectedTenant.contratosAtivos.filter(contrato => contrato.categoria === categoriaProdutoSelecionado);
    const mesmoProduto = naCategoria.find(c => c.produtoId === produtoId);
    if (mesmoProduto) return mesmoProduto;
    return naCategoria.find(c => c.tipo === 'TRIAL') ?? naCategoria[0] ?? null;
  }, [selectedTenant, categoriaProdutoSelecionado, produtoId]);
  useEffect(() => {
    let cancelled = false;
    setTenantsLoading(true);
    const timeoutId = window.setTimeout(async () => {
      try {
        const result = await dispatch(fetchEligibleTenants({
          search: tenantSearch,
          limit: TENANT_SEARCH_LIMIT
        })).unwrap();
        if (!cancelled) {
          setTenants(result);
        }
      } finally {
        if (!cancelled) {
          setTenantsLoading(false);
        }
      }
    }, TENANT_SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [dispatch, tenantSearch]);
  useEffect(() => {
    if (produtos.length === 0 && !produtosLoading) {
      dispatch(fetchActiveProducts());
    }
  }, [produtos.length, produtosLoading, dispatch]);
  useEffect(() => {
    if (!produtoSelecionado) return;
    if (expiracaoCustomizada) return;
    const dias = produtoSelecionado.trialDays && produtoSelecionado.trialDays > 0 ? produtoSelecionado.trialDays : DEFAULT_ALERT_DAYS * 2;
    setValue('dataExpiracao', addDaysIso(dataInicio || dataAtualISO(), dias), {
      shouldValidate: true
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute only when product changes
  }, [produtoSelecionado?.id]);
  const handleFormSubmit = (data: FormSchema) => {
    if (!produtoSelecionado) return;
    onSubmit({
      tenantId: data.tenantId,
      produtoId: data.produtoId,
      produtoSnapshot: {
        nome: produtoSelecionado.name,
        trialDays: produtoSelecionado.trialDays ?? null,
        trialBehavior: produtoTrialBehavior
      },
      dataInicio: data.dataInicio,
      dataExpiracao: data.dataExpiracao,
      alertaVencimentoDias: data.alertaVencimentoDias,
      observacao: data.observacao?.trim() ? data.observacao.trim() : null,
      metodoPagamento: data.metodoPagamento,
      diaVencimento: data.diaVencimento
    });
  };
  const handleCancel = () => {
    if (isDirty && !window.confirm('Você tem alterações não salvas. Deseja descartar?')) {
      return;
    }
    onCancel();
  };
  const tenantJaEmTrialAtivo = !!contratoEmConflito;
  const submitDisabled = saving || tenantJaEmTrialAtivo;
  const handleVerTrialAtual = () => {
    if (!tenantId || !contratoEmConflito) return;
    const params = new URLSearchParams({
      highlightTenant: tenantId,
      highlightProduct: contratoEmConflito.produtoId
    });
    router.push(`/admin-geral/financeiro/trials?${params.toString()}`);
  };
  return <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-6">
      <Card className="flex flex-col gap-5 p-6">
        <h3 className="text-body-bold font-body-bold text-default-font">Cliente</h3>

        <Controller name="tenantId" control={control} render={({
        field
      }) => <TenantPicker value={field.value} onValueChange={value => {
        field.onChange(value);
        const tenant = tenants.find(t => t.id === value) ?? null;
        if (tenant) {
          setSelectedTenant(tenant);
        }
      }} tenants={tenants} loading={tenantsLoading} search={tenantSearch} onSearchChange={setTenantSearch} error={!!errors.tenantId} helpText={errors.tenantId?.message} />} />
      </Card>

      <Card className="flex flex-col gap-5 p-6">
        <h3 className="text-body-bold font-body-bold text-default-font">Produto</h3>

        <Controller name="produtoId" control={control} render={({
        field
      }) => <Select label="Produto que o cliente vai testar *" placeholder={produtosLoading ? 'Carregando produtos...' : 'Selecione um produto ativo'} value={field.value} onValueChange={field.onChange} disabled={produtosLoading} error={!!errors.produtoId} helpText={errors.produtoId?.message}>
              <Select.Content>
                {produtos.map(produto => <Select.Item key={produto.id} value={produto.id}>
                    {produto.name}
                  </Select.Item>)}
              </Select.Content>
            </Select>} />

        {produtoSelecionado && <div className="border-brand-100 bg-brand-50 flex items-start gap-3 rounded-[8px] border px-4 py-3">
            <FeatherPackage className="text-brand-600 mt-0.5 h-4 w-4 shrink-0" />
            <div className="flex flex-col gap-1 text-sm">
              <span className="text-brand-800 font-semibold">
                Trial padrão do produto:{' '}
                {produtoSelecionado.trialDays && produtoSelecionado.trialDays > 0 ? `${produtoSelecionado.trialDays} dias` : 'sem trial configurado no catálogo'}
              </span>
              {produtoTrialBehavior && <span className="text-brand-700">
                  Comportamento: {TRIAL_BEHAVIOR_LABEL[produtoTrialBehavior]}
                </span>}
              <span className="text-brand-700 text-xs">
                Sugerimos a data de expiração com base nesse padrão — você pode ajustar abaixo.
              </span>
            </div>
          </div>}

        {tenantJaEmTrialAtivo && <Alert variant="warning" title="Este cliente já possui um contrato/trial ativo nesta categoria" description="Não é possível abrir um novo trial enquanto houver outro contrato/trial vigente na mesma categoria — a apuração ficaria ambígua. Para estender, encerrar ou converter o contrato/trial atual, abra-o na listagem." actions={<Button variant="neutral-secondary" size="small" icon={<FeatherExternalLink />} onClick={handleVerTrialAtual}>
                Ver contrato/trial atual
              </Button>} />}
      </Card>

      <Card className="flex flex-col gap-5 p-6">
        <h3 className="text-body-bold font-body-bold text-default-font">Período do trial</h3>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Controller name="dataInicio" control={control} render={({
          field
        }) => <TextField label="Data de início *" error={!!errors.dataInicio} helpText={errors.dataInicio?.message ?? 'Padrão: hoje'}>
                <TextField.Input type="date" value={field.value} onChange={field.onChange} />
              </TextField>} />

          <Controller name="dataExpiracao" control={control} render={({
          field
        }) => <TextField label="Data de expiração *" error={!!errors.dataExpiracao} helpText={errors.dataExpiracao?.message}>
                <TextField.Input type="date" value={field.value} onChange={event => {
            field.onChange(event);
            setExpiracaoCustomizada(true);
            if (event.target.value && dataInicio && event.target.value <= dataInicio) {
              setValue('dataExpiracao', '');
            }
          }} />
              </TextField>} />
        </div>

        {periodoDias > 0 && <div className="border-brand-100 bg-brand-50 flex items-center justify-between rounded-[8px] border px-4 py-3">
            <span className="text-brand-700 text-sm">Período total do trial</span>
            <span className="text-brand-900 text-lg font-bold">
              {periodoDias} {periodoDias === 1 ? 'dia' : 'dias'}
            </span>
          </div>}

        <Controller name="alertaVencimentoDias" control={control} render={({
        field
      }) => <TextField label="Alerta antes do vencimento (dias)" error={!!errors.alertaVencimentoDias} helpText={errors.alertaVencimentoDias?.message ?? 'Quantos dias antes do vencimento o sistema deve avisar você. Padrão: 7.'}>
              <TextField.Input type="number" placeholder="7" value={field.value != null ? String(field.value) : ''} onChange={event => field.onChange(event.target.value === '' ? null : Number(event.target.value))} />
            </TextField>} />
      </Card>

      <Card className="flex flex-col gap-5 p-6">
        <h3 className="text-body-bold font-body-bold text-default-font">
          Condições comerciais pós-trial
        </h3>
        <p className="text-caption text-subtext-color">
          Definem como o cliente será cobrado quando o trial for convertido. Pode ser editado
          depois.
        </p>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Controller name="metodoPagamento" control={control} render={({
          field
        }) => <Select label="Método de pagamento *" placeholder="Selecione" value={field.value} onValueChange={field.onChange} error={!!errors.metodoPagamento} helpText={errors.metodoPagamento?.message}>
                <Select.Content>
                  <Select.Item value="PIX">PIX</Select.Item>
                  <Select.Item value="BOLETO">Boleto</Select.Item>
                  <Select.Item value="CARTAO">Cartão</Select.Item>
                </Select.Content>
              </Select>} />
        </div>

        <Controller name="diaVencimento" control={control} render={({
        field
      }) => <TextField label="Dia de vencimento da fatura *" error={!!errors.diaVencimento} helpText={errors.diaVencimento?.message ?? 'Valor entre 1 e 28'}>
              <TextField.Input type="number" placeholder="10" value={field.value != null ? String(field.value) : ''} onChange={event => field.onChange(event.target.value === '' ? 0 : Number(event.target.value))} />
            </TextField>} />
      </Card>

      <Card className="flex flex-col gap-5 p-6">
        <h3 className="text-body-bold font-body-bold text-default-font">
          Observação <span className="text-subtext-color font-normal">(opcional)</span>
        </h3>

        <Controller name="observacao" control={control} render={({
        field
      }) => <TextArea>
              <TextArea.Input placeholder="Contexto da concessão do trial, condições negociadas, etc." value={field.value ?? ''} onChange={event => field.onChange(event.target.value || null)} rows={3} />
            </TextArea>} />
      </Card>

      <Card className="flex flex-col gap-3 p-6">
        <div className="flex items-start gap-2 text-sm text-neutral-600">
          <FeatherInfo className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" />O cliente não será
          cobrado durante o trial.
        </div>
        <div className="flex items-start gap-2 text-sm text-neutral-600">
          <FeatherAlertTriangle className="text-warning-500 mt-0.5 h-4 w-4 shrink-0" />O contrato
          passa para cobrança automática na data de expiração.
        </div>
      </Card>

      <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-neutral-200 bg-white py-4">
        <Button variant="neutral-secondary" size="medium" onClick={handleCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="brand-primary" size="medium" type="submit" loading={saving} disabled={submitDisabled}>
          Iniciar trial
        </Button>
      </div>
    </form>;
}